import { z } from 'zod'

const PUBLIC_CONFIG_ELEMENT_ID = 'public-config'
const THEME_METADATA = {
	pluxee: {
		appName: 'Pluxee Smart Marketing',
		faviconHref: '/meta/Favicon_Pluxee.ico',
	},
	kaching: {
		appName: 'Kaching For Business',
		faviconHref: '/meta/kaching_favicon.png',
	},
}

let cachedPublicConfig

const createPublicConfigError = message => new Error(`Public config error: ${message}`)

const formatZodError = error =>
	error.issues
		.map(issue => `${issue.path.length > 0 ? issue.path.join('.') : 'root'}: ${issue.message}`)
		.join('; ')

const nullableTrimmedString = z.preprocess(value => {
	if (value === null || value === undefined) {
		return null
	}

	if (typeof value === 'string') {
		return value.trim()
	}

	return value
}, z.union([z.string().min(1), z.null()]))

const nullableUrl = z.preprocess(value => {
	if (value === null || value === undefined) {
		return null
	}

	if (typeof value === 'string') {
		return value.trim()
	}

	return value
}, z.union([z.string().url(), z.null()]))

const publicConfigSchema = z.object({
	buildString: nullableTrimmedString,
	budgetLowNoticeDays: z.union([z.number().int(), z.null()]),
	googleAnalyticsTrackingId: nullableTrimmedString,
	graphqlEndpoint: z.string().trim().url(),
	identityService: z.string().trim().url(),
	requestAcceptLanguage: nullableTrimmedString,
	salesContactEmail: z.string().trim().min(1),
	sentryDsn: nullableUrl,
	showSummary: z.boolean(),
	stripe: z.object({
		mode: nullableTrimmedString,
		publishableKeyFi: nullableTrimmedString,
		publishableKeySe: nullableTrimmedString,
	}),
	supportEmailsByMarket: z.record(z.string(), z.string().trim().min(1)),
	theme: z.enum(['pluxee', 'kaching']),
	wrappEnv: nullableTrimmedString,
})

const getDocument = () => (typeof document !== 'undefined' ? document : null)

const readPublicConfigScript = () => {
	const documentObject = getDocument()
	if (!documentObject) {
		throw createPublicConfigError('Public config is unavailable outside the browser')
	}

	const script = documentObject.getElementById(PUBLIC_CONFIG_ELEMENT_ID)
	if (!script) {
		throw createPublicConfigError(`Missing public config script "#${PUBLIC_CONFIG_ELEMENT_ID}"`)
	}

	try {
		return JSON.parse(script.textContent || '{}')
	} catch {
		throw createPublicConfigError('Public config payload is not valid JSON')
	}
}

const normalizePublicConfig = publicConfig => {
	const result = publicConfigSchema.safeParse(publicConfig)

	if (!result.success) {
		throw createPublicConfigError(formatZodError(result.error))
	}

	return {
		...result.data,
		themeMetadata: THEME_METADATA[result.data.theme],
	}
}

export const initializePublicConfig = () => {
	if (cachedPublicConfig) {
		return cachedPublicConfig
	}

	cachedPublicConfig = normalizePublicConfig(readPublicConfigScript())
	return cachedPublicConfig
}

export const getPublicConfig = () => cachedPublicConfig || initializePublicConfig()

export const applyThemeMetadata = (publicConfig = getPublicConfig()) => {
	const documentObject = getDocument()
	if (!documentObject) {
		return
	}

	const favicon = documentObject.querySelector("link[rel~='icon']")
	const appName = documentObject.querySelector("meta[name='application-name']")
	const mobileAppName = documentObject.querySelector("meta[name='apple-mobile-web-app-title']")

	if (favicon) {
		favicon.href = publicConfig.themeMetadata.faviconHref
	}

	if (appName) {
		appName.content = publicConfig.themeMetadata.appName
	}

	if (mobileAppName) {
		mobileAppName.content = publicConfig.themeMetadata.appName
	}
}

export const resetPublicConfigForTests = () => {
	cachedPublicConfig = undefined
}

export const appConfig = getPublicConfig()
